// Auto-save photos to device.
// Three back-ends, picked at runtime:
//
//   1. Capacitor APK (Android)
//        → @capacitor/filesystem writes the JPEG into Documents/RealSelfie/.
//          No folder picker, no permission UI dance — saving is silent and
//          fully automatic on the first photo.
//
//   2. Chrome / Edge desktop & Android browser with File System Access API
//        → user picks a folder once, app creates/uses a "RealSelfie"
//          subdirectory and writes every photo there silently.
//
//   3. Any other browser (iOS Safari, Firefox)
//        → fallback: trigger a normal `<a download>` for the JPEG so the
//          user can save it via the browser's Downloads UI.
//
import { isCapacitorNative } from "./platform";

const DB_NAME = "realselfie-fs";
const STORE = "handles";
const HANDLE_KEY = "savedir";

let _dbPromise = null;
function openHandleDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function putHandle(handle) {
  const db = await openHandleDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(handle, HANDLE_KEY);
    tx.oncomplete = res;
    tx.onerror = () => rej(tx.error);
  });
}

async function readHandle() {
  const db = await openHandleDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(HANDLE_KEY);
    req.onsuccess = () => res(req.result || null);
    req.onerror = () => rej(req.error);
  });
}

export async function clearSavedHandle() {
  const db = await openHandleDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(HANDLE_KEY);
    tx.oncomplete = res;
    tx.onerror = () => rej(tx.error);
  });
}

export function isFsApiSupported() {
  // Capacitor Filesystem is always supported in the APK. In a plain browser
  // we fall back to the File System Access API (Chrome / Edge).
  if (isCapacitorNative()) return true;
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

async function verifyPermission(handle, mode = "readwrite") {
  if (!handle?.queryPermission) return true;
  try {
    const perm = await handle.queryPermission({ mode });
    if (perm === "granted") return true;
    const req = await handle.requestPermission({ mode });
    return req === "granted";
  } catch {
    return false;
  }
}

// MUST be called from a user gesture (click handler).
// In Capacitor APK: no-op (saving is automatic). Returns a sentinel object.
// In Chrome/Edge: opens the directory picker.
export async function pickRealSelfieFolder() {
  if (isCapacitorNative()) {
    // Persist a sentinel so the UI shows auto-save as enabled. The actual
    // path used is Documents/RealSelfie via Capacitor Filesystem.
    await putHandle({ __native: true, name: "RealSelfie (Documents)" });
    return { __native: true, name: "RealSelfie (Documents)" };
  }
  if (!isFsApiSupported()) return null;
  try {
    const parent = await window.showDirectoryPicker({
      id: "realselfie-root",
      mode: "readwrite",
      startIn: "pictures",
    });
    let target = parent;
    try {
      target = await parent.getDirectoryHandle("RealSelfie", { create: true });
    } catch {
      /* keep parent */
    }
    await putHandle(target);
    return target;
  } catch (e) {
    if (e?.name === "AbortError") return null;
    console.warn("pickRealSelfieFolder error:", e);
    return null;
  }
}

export async function getActiveFolder() {
  const handle = await readHandle();
  if (!handle) return null;
  if (handle.__native) return handle; // Capacitor sentinel — no permission check
  const ok = await verifyPermission(handle);
  if (!ok) return null;
  return handle;
}

export function buildFilename(ts = Date.now()) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `RealSelfie_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}.jpg`
  );
}

async function writeToFolder(handle, blob, filename) {
  const fileHandle = await handle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Convert Blob -> base64 string (no data: prefix). Required by
// Capacitor Filesystem.writeFile.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

// Native (Capacitor) save path — writes the JPEG into Documents/RealSelfie/.
// On Android the file shows up under "Internal storage › Documents › RealSelfie"
// in the system Files app and is picked up by gallery scanners that watch
// the public Documents directory.
async function saveViaCapacitor(blob, filename) {
  // Dynamic import keeps the web bundle small for non-native users.
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const data = await blobToBase64(blob);
  const res = await Filesystem.writeFile({
    path: `RealSelfie/${filename}`,
    data,
    directory: Directory.Documents,
    recursive: true,
  });
  return res?.uri || `Documents/RealSelfie/${filename}`;
}

// Main entry. Returns { method, folderName?, filename, uri? }.
export async function autoSavePhoto(blob) {
  const filename = buildFilename();

  // 1) Native APK → Capacitor Filesystem
  if (isCapacitorNative()) {
    try {
      const uri = await saveViaCapacitor(blob, filename);
      return { method: "native", folderName: "Documents/RealSelfie", filename, uri };
    } catch (e) {
      console.warn("Capacitor Filesystem write failed, falling back:", e);
      // fall through to download fallback
    }
  }

  // 2) Browser with File System Access API
  const handle = await getActiveFolder();
  if (handle && !handle.__native) {
    try {
      await writeToFolder(handle, blob, filename);
      return { method: "folder", folderName: handle.name || "RealSelfie", filename };
    } catch (e) {
      console.warn("Folder write failed, falling back to download:", e);
    }
  }

  // 3) Anchor-download fallback
  downloadBlob(blob, filename);
  return { method: "download", filename };
}
